import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { System, SystemInsert, SystemUpdate, SYSTEM_FUNCTIONS } from '@/types/system';
import { useSystemStore } from '@/stores/systemStore';
import { useAuth } from '@/hooks/useAuth';
import { Info, X, Check, AlertCircle, Network } from 'lucide-react';
import { toast } from 'sonner';
import { validateCIDR, validateVLAN, validateGateway, isValidCIDR, isValidVLAN, isValidGateway } from '@/utils/networkValidation';
import { UserSelector } from '@/components/shared/UserSelector';

interface SystemFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  system?: System;
  zoneId: string;
}

const PROTOCOL_OPTIONS = ['TCP', 'UDP', 'HTTP', 'HTTPS', 'MQTT', 'OPC-UA', 'Modbus', 'EtherNet/IP', 'PROFINET', 'BACnet'];
const PII_TYPE_OPTIONS = ['Name', 'Email', 'Phone', 'Address', 'ID Number', 'Financial', 'Health', 'Biometric'];

export function SystemFormDialog({ open, onOpenChange, system, zoneId }: SystemFormDialogProps) {
  const { addSystem, updateSystem } = useSystemStore();
  const { profile } = useAuth();
  
  const [name, setName] = useState('');
  const [functionType, setFunctionType] = useState('');
  const [protocols, setProtocols] = useState<string[]>([]);
  const [ownerId, setOwnerId] = useState<string | undefined>();
  const [rto, setRto] = useState<number | undefined>();
  const [rpo, setRpo] = useState<number | undefined>();
  const [processesPii, setProcessesPii] = useState(false);
  const [piiTypes, setPiiTypes] = useState<string[]>([]);
  const [dpiaRequired, setDpiaRequired] = useState(false);
  
  // Network configuration
  const [cidr, setCidr] = useState('');
  const [cidrError, setCidrError] = useState<string | undefined>();
  const [vlanId, setVlanId] = useState('');
  const [vlanError, setVlanError] = useState<string | undefined>();
  const [defaultGateway, setDefaultGateway] = useState('');
  const [gatewayError, setGatewayError] = useState<string | undefined>();
  
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (system) {
      setName(system.name);
      setFunctionType(system.function || '');
      setProtocols(system.protocols || []);
      setOwnerId(system.owner_id);
      setRto(system.rto || undefined);
      setRpo(system.rpo || undefined);
      setProcessesPii(system.processes_pii || false);
      setPiiTypes(system.pii_types || []);
      setDpiaRequired(system.dpia_required || false);
      setCidr(system.cidr || '');
      setVlanId(system.vlan_id?.toString() || '');
      setDefaultGateway(system.default_gateway || '');
    } else {
      resetForm();
    }
  }, [system, open]);

  const resetForm = () => {
    setName('');
    setFunctionType('');
    setProtocols([]);
    setOwnerId(undefined);
    setRto(undefined);
    setRpo(undefined);
    setProcessesPii(false);
    setPiiTypes([]);
    setDpiaRequired(false);
    setCidr('');
    setCidrError(undefined);
    setVlanId('');
    setVlanError(undefined);
    setDefaultGateway('');
    setGatewayError(undefined);
  };

  const toggleProtocol = (protocol: string) => {
    setProtocols(prev =>
      prev.includes(protocol) ? prev.filter(p => p !== protocol) : [...prev, protocol]
    );
  };

  const togglePiiType = (type: string) => {
    setPiiTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  const handleCidrChange = (value: string) => {
    setCidr(value);
    if (value.trim()) {
      const result = validateCIDR(value);
      setCidrError(result.valid ? undefined : result.message);
    } else {
      setCidrError(undefined);
    }
  };

  const handleVlanChange = (value: string) => {
    setVlanId(value);
    if (value.trim()) {
      const result = validateVLAN(value);
      setVlanError(result.valid ? undefined : result.message);
    } else {
      setVlanError(undefined);
    }
  };

  const handleGatewayChange = (value: string) => {
    setDefaultGateway(value);
    if (value.trim()) {
      const result = validateGateway(value);
      setGatewayError(result.valid ? undefined : result.message);
    } else {
      setGatewayError(undefined);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error('System name is required');
      return;
    }
    
    if (!profile?.org_id) {
      toast.error('Organization not found');
      return;
    }

    // Validate network fields
    if (cidr.trim() && !isValidCIDR(cidr)) {
      toast.error('Invalid CIDR format');
      return;
    }
    if (vlanId.trim() && !isValidVLAN(vlanId)) {
      toast.error('Invalid VLAN ID (must be 1-4094)');
      return;
    }
    if (defaultGateway.trim() && !isValidGateway(defaultGateway)) {
      toast.error('Invalid default gateway IP');
      return;
    }

    setSubmitting(true);
    
    try {
      const systemData: SystemInsert | SystemUpdate = {
        name: name.trim(),
        function: functionType || undefined,
        protocols: protocols.length > 0 ? protocols : undefined,
        owner_id: ownerId,
        rto: rto || undefined,
        rpo: rpo || undefined,
        processes_pii: processesPii,
        pii_types: processesPii && piiTypes.length > 0 ? piiTypes : undefined,
        dpia_required: dpiaRequired,
        cidr: cidr.trim() || undefined,
        vlan_id: vlanId.trim() ? parseInt(vlanId, 10) : undefined,
        default_gateway: defaultGateway.trim() || undefined,
      };

      if (system) {
        await updateSystem(system.id, systemData as SystemUpdate);
        toast.success('System updated successfully');
      } else {
        await addSystem({
          ...systemData,
          zone_id: zoneId,
          org_id: profile.org_id,
        } as SystemInsert);
        toast.success('System created successfully');
      }
      
      onOpenChange(false);
      resetForm();
    } catch (error) {
      console.error('Error saving system:', error);
      toast.error(system ? 'Failed to update system' : 'Failed to create system');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{system ? 'Edit System' : 'Create New System'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="font-medium text-sm">Basic Information</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="name">System Name *</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Active Directory Server"
                  required
                />
              </div>

              <div className="col-span-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor="function">Function</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>The primary function or purpose of this system</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Select value={functionType} onValueChange={setFunctionType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select function" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(SYSTEM_FUNCTIONS).map(([category, functions]) => (
                      <optgroup key={category} label={category}>
                        {functions.map((func) => (
                          <SelectItem key={func} value={func}>
                            {func}
                          </SelectItem>
                        ))}
                      </optgroup>
                    ))}
                  </SelectContent>
              </Select>
              </div>

              <div>
                <UserSelector
                  value={ownerId}
                  onChange={setOwnerId}
                  label="Owner"
                  placeholder="Select owner"
                />
              </div>
            </div>
          </div>

          {/* Network Configuration */}
          <div className="space-y-4 p-3 border rounded-md bg-muted/30">
            <h3 className="font-medium text-sm flex items-center gap-2">
              <Network className="h-4 w-4" />
              Network Configuration
            </h3>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cidr">Subnet (CIDR)</Label>
                <div className="relative">
                  <Input
                    id="cidr"
                    placeholder="e.g., 10.0.10.0/24"
                    value={cidr}
                    onChange={(e) => handleCidrChange(e.target.value)}
                    className={cidrError ? 'border-destructive pr-10' : cidr && !cidrError ? 'border-green-500 pr-10' : ''}
                  />
                  {cidr && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {cidrError ? (
                        <AlertCircle className="h-4 w-4 text-destructive" />
                      ) : (
                        <Check className="h-4 w-4 text-green-500" />
                      )}
                    </div>
                  )}
                </div>
                {cidrError && <p className="text-xs text-destructive">{cidrError}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="vlan">VLAN ID</Label>
                <div className="relative">
                  <Input
                    id="vlan"
                    type="number"
                    min="1"
                    max="4094"
                    placeholder="1-4094"
                    value={vlanId}
                    onChange={(e) => handleVlanChange(e.target.value)}
                    className={vlanError ? 'border-destructive pr-10' : vlanId && !vlanError ? 'border-green-500 pr-10' : ''}
                  />
                  {vlanId && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {vlanError ? (
                        <AlertCircle className="h-4 w-4 text-destructive" />
                      ) : (
                        <Check className="h-4 w-4 text-green-500" />
                      )}
                    </div>
                  )}
                </div>
                {vlanError && <p className="text-xs text-destructive">{vlanError}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="gateway">Default Gateway</Label>
                <div className="relative">
                  <Input
                    id="gateway"
                    placeholder="e.g., 10.0.10.1"
                    value={defaultGateway}
                    onChange={(e) => handleGatewayChange(e.target.value)}
                    className={gatewayError ? 'border-destructive pr-10' : defaultGateway && !gatewayError ? 'border-green-500 pr-10' : ''}
                  />
                  {defaultGateway && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {gatewayError ? (
                        <AlertCircle className="h-4 w-4 text-destructive" />
                      ) : (
                        <Check className="h-4 w-4 text-green-500" />
                      )}
                    </div>
                  )}
                </div>
                {gatewayError && <p className="text-xs text-destructive">{gatewayError}</p>}
              </div>
            </div>
          </div>

          {/* Protocols */}
          <div className="space-y-4">
            <h3 className="font-medium text-sm">Network Protocols</h3>
            <div className="flex flex-wrap gap-2">
              {PROTOCOL_OPTIONS.map((protocol) => (
                <Badge
                  key={protocol}
                  variant={protocols.includes(protocol) ? 'default' : 'outline'}
                  className="cursor-pointer"
                  onClick={() => toggleProtocol(protocol)}
                >
                  {protocol}
                  {protocols.includes(protocol) && <X className="ml-1 h-3 w-3" />}
                </Badge>
              ))}
            </div>
          </div>

          {/* Recovery Objectives */}
          <div className="space-y-4">
            <h3 className="font-medium text-sm">Recovery Objectives</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="rto">RTO (hours)</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Recovery Time Objective: Maximum acceptable downtime</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Input
                  id="rto"
                  type="number"
                  min="0"
                  value={rto || ''}
                  onChange={(e) => setRto(e.target.value ? parseInt(e.target.value) : undefined)}
                  placeholder="e.g., 4"
                />
              </div>

              <div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="rpo">RPO (hours)</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Recovery Point Objective: Maximum acceptable data loss</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Input
                  id="rpo"
                  type="number"
                  min="0"
                  value={rpo || ''}
                  onChange={(e) => setRpo(e.target.value ? parseInt(e.target.value) : undefined)}
                  placeholder="e.g., 1"
                />
              </div>
            </div>
          </div>

          {/* Privacy & Data Protection */}
          <div className="space-y-4">
            <h3 className="font-medium text-sm">Privacy & Data Protection</h3>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="processes_pii"
                checked={processesPii}
                onCheckedChange={(checked) => {
                  setProcessesPii(checked as boolean);
                  if (!checked) {
                    setPiiTypes([]);
                    setDpiaRequired(false);
                  }
                }}
              />
              <Label htmlFor="processes_pii" className="font-normal cursor-pointer">
                This system processes Personal Identifiable Information (PII)
              </Label>
            </div>

            {processesPii && (
              <>
                <div>
                  <Label>PII Types Processed</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {PII_TYPE_OPTIONS.map((type) => (
                      <Badge
                        key={type}
                        variant={piiTypes.includes(type) ? 'default' : 'outline'}
                        className="cursor-pointer"
                        onClick={() => togglePiiType(type)}
                      >
                        {type}
                        {piiTypes.includes(type) && <X className="ml-1 h-3 w-3" />}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="dpia_required"
                    checked={dpiaRequired}
                    onCheckedChange={(checked) => setDpiaRequired(checked as boolean)}
                  />
                  <Label htmlFor="dpia_required" className="font-normal cursor-pointer">
                    Data Protection Impact Assessment (DPIA) required
                  </Label>
                </div>
              </>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenChange(false);
                resetForm();
              }}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving...' : system ? 'Update System' : 'Create System'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
